import React from "react";
import { Helmet } from "react-helmet-async";

const formattedDate = new Date().toLocaleDateString("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

// ─── Shared Section Card ────────────────────────────────────────────────────
export function PolicySection({ id, title, children, variant = "default" }) {
  const isWarning = variant === "warning";

  return (
    <section id={id} className="mb-4 scroll-mt-32">
      <div
        className={`flex rounded-xl overflow-hidden border shadow-xs ${
          isWarning
            ? "border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/20"
            : "border-border bg-card"
        }`}
      >
        {/* Left accent strip */}
        <div
          className={`w-1 flex-shrink-0 ${
            isWarning ? "bg-red-500" : "bg-accent"
          }`}
        />

        <div className="flex-1 px-6 py-5 min-w-0">
          {/* Section header */}
          <h2
            className={`font-heading font-bold text-[15px] uppercase tracking-wide mb-3 ${
              isWarning
                ? "text-red-700 dark:text-red-400"
                : "text-foreground"
            }`}
          >
            {title}
          </h2>

          {/* Section body */}
          <div
            className={`text-[15px] leading-7 space-y-3 ${
              isWarning
                ? "text-red-700 dark:text-red-300"
                : "text-muted-fg"
            } [&_strong]:font-semibold [&_strong]:text-foreground`}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Page Layout Shell ───────────────────────────────────────────────────────
export default function PolicyLayout({
  title,
  description,
  canonical,
  sections,
  children,
  relatedLinks,
}) {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>{title} | Khoja Distributions</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── Hero header ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-brand-navy via-[#162032] to-brand-navy-mid py-14 px-6 relative overflow-hidden">
        {/* Subtle dot grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, white 1.5px, transparent 1.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="max-w-3xl mx-auto relative">
          {/* Breadcrumb pill */}
          <div className="inline-flex items-center gap-1.5 bg-white/10 text-sky-300 text-[11px] font-heading font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5">
            <span>Khoja Distributions</span>
            <span className="opacity-50">›</span>
            <span>Information</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-heading font-black text-white mb-3 leading-tight">
            {title}
          </h1>
          <p className="text-slate-400 text-sm">
            Last updated: {formattedDate}
          </p>
        </div>
      </div>

      {/* ── Quick-nav pills ──────────────────────────────────────────────── */}
      {sections && sections.length > 0 && (
        <div className="bg-card border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-3">
            <div
              className="flex gap-2 overflow-x-auto pb-0.5"
              style={{ scrollbarWidth: "none" }}
            >
              {sections.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="flex-shrink-0 text-[11px] font-heading font-semibold text-muted-fg bg-muted px-3 py-1.5 rounded-full hover:bg-accent hover:text-on-accent transition-colors whitespace-nowrap"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        {children}

        {relatedLinks && (
          <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-4">
            {relatedLinks}
          </div>
        )}
      </div>
    </div>
  );
}
